import React, { useEffect, useRef, useState } from 'react';
import { formatWhen, uploadInquiryPhoto } from './inquiryShared';

// Conversation bubbles. `viewer` is 'customer' or 'staff' — your own side is on the right.
export function MessageThread({ messages, viewer, emptyText }) {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }); }, [messages.length]);

  if (!messages.length) return <p style={{ color: '#9a9a9a', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>{emptyText}</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {messages.map(m => {
        if (m.sender_type === 'system') {
          return <div key={m.id} style={{ textAlign: 'center', fontSize: 12, color: '#9a9a9a' }}>{m.body} · {formatWhen(m.created_at)}</div>;
        }
        const mine = (viewer === 'customer') === (m.sender_type === 'customer');
        const who = m.sender_type === 'customer' ? (viewer === 'customer' ? 'You' : 'Customer') : (m.staff_name || 'Premier Pawn');
        return (
          <div key={m.id} style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
            <div style={{ fontSize: 11, color: '#9a9a9a', marginBottom: 3, textAlign: mine ? 'right' : 'left' }}>{who} · {formatWhen(m.created_at)}</div>
            <div style={{ background: mine ? '#1a2744' : '#f1efe7', color: mine ? '#fff' : '#333', borderRadius: 12, padding: '10px 14px', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {m.body}
              {m.images?.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {m.images.map(img => (
                    <a key={img.id} href={img.url} target="_blank" rel="noreferrer">
                      <img src={img.url} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6 }} />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
}

// Text box + optional photos. onSend({ body, images }) should return a promise.
export function ReplyBox({ onSend, placeholder, allowPhotos = true, sendLabel = 'Send' }) {
  const [body, setBody] = useState('');
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 8 - images.length);
    e.target.value = '';
    if (!files.length) return;
    setBusy(true); setError('');
    try {
      for (const f of files) {
        const img = await uploadInquiryPhoto(f);
        setImages(prev => [...prev, img]);
      }
    } catch { setError('Photo upload failed.'); }
    setBusy(false);
  };

  const send = async () => {
    if (!body.trim() && !images.length) return;
    setBusy(true); setError('');
    try {
      await onSend({ body: body.trim(), images });
      setBody(''); setImages([]);
    } catch (e) { setError(e.response?.data?.error || 'Could not send. Please try again.'); }
    setBusy(false);
  };

  return (
    <div>
      {error && <div className="alert alert-error" style={{ marginBottom: 8 }}>{error}</div>}
      <textarea className="form-control" rows={3} value={body} placeholder={placeholder}
        onChange={e => setBody(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }} />
      {images.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {images.map((img, i) => (
            <div key={img.public_id || i} style={{ position: 'relative' }}>
              <img src={img.url} alt="" style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 6 }} />
              <button type="button" onClick={() => setImages(prev => prev.filter((_, idx) => idx !== i))}
                style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: '#c62828', color: '#fff', border: '2px solid #fff', fontSize: 10, lineHeight: 1, cursor: 'pointer' }}>✕</button>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, gap: 8 }}>
        {allowPhotos ? (
          <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
            📷 Add photos
            <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={addPhotos} disabled={busy} />
          </label>
        ) : <span />}
        <button type="button" className="btn btn-primary btn-sm" onClick={send} disabled={busy || (!body.trim() && !images.length)}>
          {busy ? 'Sending…' : sendLabel}
        </button>
      </div>
    </div>
  );
}
